/*  CourseSetup — Create New Course (iter100.113 Phase 4).
    ────────────────────────────────────────────────────────────
    3-column layout (per HTML mockup, translated to Invisique palette):
      • Left rail    (220px): section-nav with progress badges
      • Main           (fluid): accordion cards for the 4 sections
      • Right rail   (280px): live Course Summary + Completion checklist

    4 sections (renumbered from the 7-section blueprint — 4/5/7 deferred):
      1. Basic Information      (Name · Type · Track · Description · Thumbnail)
      2. Course Structure       (Phase · Duration · Content Format · Tone · Total)
      3. Assessment Settings    (Quiz defaults · Retakes · Final Exam)
      4. Publishing & Visibility (Status · Publish Date · Expiry Date)

    Auto-saves 2s after edits stop. Topbar CTA is "Save & Continue" on
    new courses, "Save changes" on edit. Publish is handled elsewhere
    (Submit-for-Review lives on the Builder screen — this screen is metadata).
*/
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import {
    ArrowLeft, FloppyDisk, CheckCircle, CaretDown, ClipboardText, Stack,
    ClockCounterClockwise, Gear, Question as QuestionIcon, Tooth, IdentificationCard,
    Users, PencilSimple, ImageSquare, VideoCamera, Article, FileArrowUp,
    Sparkle, Info, Warning, CircleDashed, PaperPlaneTilt, LockKey,
    Circle,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import ThumbnailUploader from "@/components/creator/ThumbnailUploader";
import RichTextEditor from "@/components/creator/RichTextEditor";
import CourseActivityFeed from "@/components/creator/CourseActivityFeed";


// ─── Enum options ─────────────────────────────────────────────────────

const COURSE_TYPES = [
    { value: "foundation_modules",         label: "Foundation Modules",        desc: "Foundational curriculum for GPs & Orthos" },
    { value: "starter_case_workshop",      label: "Starter Case Workshop",     desc: "Hands-on first-case walkthrough" },
    { value: "clinical_planning_workshop", label: "Clinical Planning Workshop", desc: "Advanced plan review & validation" },
    { value: "provider_growth_program",    label: "Provider Growth Program",   desc: "Practice-building & marketing skills" },
];

const LEARNING_TRACKS = [
    { value: "gp",        label: "General Dentist", Icon: Tooth },
    { value: "ortho",     label: "Orthodontist",    Icon: IdentificationCard },
    { value: "pediatric", label: "Pediatric",       Icon: Users },
    { value: "both",      label: "All Tracks",      Icon: Sparkle },
];

const PHASES = [
    { value: "foundation", label: "Phase 1 · Foundation",  desc: "Core curriculum, self-paced" },
    { value: "assessment", label: "Phase 2 · Assessment",  desc: "Quiz-driven knowledge checks" },
    { value: "validation", label: "Phase 3 · Validation",  desc: "Case submission + clinical review" },
];

const CONTENT_TONES = [
    { value: "clinical_technical", label: "Clinical / Technical (peer-to-peer)" },
    { value: "instructional",      label: "Instructional (structured teaching)" },
    { value: "hybrid",              label: "Hybrid (mix of both)" },
];

const QUIZ_TYPES = [
    { value: "hybrid",         label: "Hybrid — MCQ + Clinical Scenarios" },
    { value: "mcq_only",       label: "MCQ Only" },
    { value: "scenario_only",  label: "Clinical Scenario Only" },
];

// Recommended Phase(s) for each Course Type. A mismatch triggers a soft-warn
// banner in the Structure section — it doesn't block save, just nudges the
// author to double-check the pairing. See PRD 2026-07-15 for rationale.
const COURSE_TYPE_PHASE_HINTS = {
    foundation_modules:         { phases: ["foundation"],                label: "Phase 1 · Foundation" },
    starter_case_workshop:      { phases: ["foundation", "assessment"],  label: "Phase 1 · Foundation or Phase 2 · Assessment" },
    clinical_planning_workshop: { phases: ["assessment", "validation"],  label: "Phase 2 · Assessment or Phase 3 · Validation" },
    provider_growth_program:    { phases: ["foundation"],                label: "Phase 1 · Foundation" },
};

const LESSON_TYPE_META = {
    video: { label: "Video", Icon: VideoCamera },
    text:  { label: "Text",  Icon: Article },
    file:  { label: "File",  Icon: FileArrowUp },
    quiz:  { label: "Quiz",  Icon: QuestionIcon },
};


const EMPTY_FORM = {
    title: "",
    description: "",
    thumbnail_media_id: null,
    duration_minutes: "",   // stored as string for the input; converted on save
    course_type: "",
    learning_tracks: [],
    phase: "",
    content_tone: "",
    quiz_default_type: "",
    quiz_default_questions_per_module: "",
    quiz_default_pass_score: "",           // percentage 0-100 in UI
    quiz_default_max_retakes: "",
    has_final_exam: false,
    final_exam_question_count: "",
    final_exam_pass_score: "",             // percentage 0-100 in UI
    publish_at: "",
    expires_at: "",
    // Section 4 — Access & Eligibility
    prerequisite_course_ids: [],
    enrollment_type: "open",
    // Section 5 — Pricing & CE Credits
    is_free: true,
    // Price in rupees (₹) for the author-facing UI. Persisted as paise on the server.
    price_amount_rupees: "",
    currency: "INR",
    ce_credits: "",
    ce_authority: "",
    // Section 7 — Case Validation
    case_validation_required: false,
    case_validation_notes: "",
    // iter100.156g — Admin-curated Recommended pin.
    is_featured: false,
};

const SECTIONS = [
    { key: "basic",       Icon: ClipboardText,          title: "Basic Information",       subtitle: "Name, type & description",      required: true  },
    { key: "structure",   Icon: Stack,                  title: "Course Structure",        subtitle: "Phase, duration & format",      required: true  },
    { key: "access",      Icon: LockKey,                title: "Access & Eligibility",    subtitle: "Prerequisites & enrollment",    required: false },
    { key: "quiz",        Icon: QuestionIcon,           title: "Assessment Settings",     subtitle: "Quiz defaults & retake rules",  required: true  },
    { key: "pricing",     Icon: Sparkle,                title: "Pricing & CE Credits",    subtitle: "Free or Paid · CE claims",      required: false },
    { key: "validation",  Icon: Warning,                title: "Case Validation",         subtitle: "Clinical review settings",      required: false },
    { key: "publish",     Icon: Gear,                   title: "Publishing & Visibility", subtitle: "Status, schedule & expiry",     required: true  },
];


export default function CourseSetup() {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isEdit = !!courseId;
    const isAdmin = user?.role === "admin" || user?.role === "super_admin";

    const [form, setForm] = useState(EMPTY_FORM);
    const [status, setStatus] = useState("draft");
    const [modules, setModules] = useState([]);       // pull for the auto-derived Content Format chip strip
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [openSections, setOpenSections] = useState({
        basic: true, structure: false, access: false, quiz: false,
        pricing: false, validation: false, publish: false,
    });
    const [activeSection, setActiveSection] = useState("basic");
    // Section 4 — Available courses to pick as prerequisites (fetched lazily
    // on first render of edit mode; filters out the current course).
    const [availableCourses, setAvailableCourses] = useState([]);

    // Auto-save throttling
    const dirtyRef = useRef(false);
    const saveTimerRef = useRef(null);

    const canEdit = status === "draft" || status === "rejected" || !isEdit;

    const load = useCallback(async () => {
        if (!isEdit) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/course-builder/courses/${courseId}`);
            setForm({
                title: data.title || "",
                description: data.description || "",
                thumbnail_media_id: data.thumbnail_media_id || null,
                duration_minutes: data.duration_minutes ?? "",
                course_type: data.course_type || "",
                learning_tracks: data.learning_tracks || [],
                phase: data.phase || "",
                content_tone: data.content_tone || "",
                quiz_default_type: data.quiz_default_type || "",
                quiz_default_questions_per_module: data.quiz_default_questions_per_module ?? "",
                quiz_default_pass_score:
                    data.quiz_default_pass_score != null
                        ? Math.round(data.quiz_default_pass_score * 100)
                        : "",
                quiz_default_max_retakes: data.quiz_default_max_retakes ?? "",
                has_final_exam: !!data.has_final_exam,
                final_exam_question_count: data.final_exam_question_count ?? "",
                final_exam_pass_score:
                    data.final_exam_pass_score != null
                        ? Math.round(data.final_exam_pass_score * 100)
                        : "",
                publish_at: (data.publish_at || "").slice(0, 10),   // yyyy-mm-dd for <input type=date>
                expires_at: (data.expires_at || "").slice(0, 10),
                prerequisite_course_ids: data.prerequisite_course_ids || [],
                enrollment_type: data.enrollment_type || "open",
                is_free: data.is_free !== false,
                price_amount_rupees: data.price_amount_cents != null ? (data.price_amount_cents / 100) : "",
                currency: data.currency || "INR",
                ce_credits: data.ce_credits ?? "",
                ce_authority: data.ce_authority || "",
                case_validation_required: !!data.case_validation_required,
                case_validation_notes: data.case_validation_notes || "",
                is_featured: !!data.is_featured,
            });
            setStatus(data.status || "draft");
            setModules(data.modules || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load course.");
            navigate("/creator");
        } finally {
            setLoading(false);
        }
    }, [courseId, isEdit, navigate]);

    useEffect(() => { load(); }, [load]);

    // Fetch the list of my other courses to offer as prerequisites.
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/course-builder/courses");
                setAvailableCourses((data || []).filter((c) => c.id !== courseId));
            } catch { /* silent — prerequisites picker just shows empty */ }
        })();
    }, [courseId]);

    useEffect(() => {
        const prev = document.title;
        document.title = isEdit ? "Edit Course — Certification Author" : "Create New Course — Certification Author";
        return () => { document.title = prev; };
    }, [isEdit]);

    // Auto-save (only in edit mode; new courses need explicit "Save & Continue")
    useEffect(() => {
        if (!isEdit || !dirtyRef.current || !canEdit) return;
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => { autoSave(); }, 2000);
        return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
    }, [form, isEdit, canEdit]);

    const set = useCallback((patch) => {
        setForm((prev) => ({ ...prev, ...patch }));
        dirtyRef.current = true;
    }, []);

    const toggleTrack = (value) => {
        set({
            learning_tracks: form.learning_tracks.includes(value)
                ? form.learning_tracks.filter((v) => v !== value)
                : [...form.learning_tracks, value],
        });
    };

    // Auto-derive Content Format from actual lesson types across modules
    const derivedContentFormat = useMemo(() => {
        const counts = { video: 0, text: 0, file: 0, quiz: 0 };
        modules.forEach((m) => (m.lessons || []).forEach((l) => {
            if (counts[l.type] !== undefined) counts[l.type] += 1;
        }));
        return Object.entries(counts).filter(([, n]) => n > 0);
    }, [modules]);

    // Auto-derive Duration from Σ lesson.duration_minutes across every module.
    // Authors can leave the manual `duration_minutes` field blank and this
    // roll-up is used automatically (saved on the course as a snapshot so
    // downstream consumers — catalogue chip, cert page, admin — read one field).
    const computedDurationMinutes = useMemo(() => {
        let total = 0;
        modules.forEach((m) => (m.lessons || []).forEach((l) => {
            const d = Number(l.duration_minutes);
            if (Number.isFinite(d) && d > 0) total += d;
        }));
        return total;
    }, [modules]);
    const displayDurationMinutes = form.duration_minutes === ""
        ? computedDurationMinutes
        : Number(form.duration_minutes);

    const buildPayload = () => {
        // Auto-fill duration_minutes with the computed sum if the author left
        // the field blank AND the roll-up is non-zero. This keeps `courses.duration_minutes`
        // as a persisted snapshot for downstream consumers (catalogue chip, cert PDF, etc).
        const manualDuration = form.duration_minutes === "" ? null : Number(form.duration_minutes);
        const finalDuration =
            manualDuration != null
                ? manualDuration
                : computedDurationMinutes > 0
                    ? computedDurationMinutes
                    : null;
        const p = {
            title: form.title.trim(),
            description: form.description || "",
            thumbnail_media_id: form.thumbnail_media_id || null,
            duration_minutes: finalDuration,
            course_type: form.course_type || null,
            learning_tracks: form.learning_tracks || [],
            phase: form.phase || null,
            content_tone: form.content_tone || null,
            quiz_default_type: form.quiz_default_type || null,
            quiz_default_questions_per_module:
                form.quiz_default_questions_per_module === "" ? null : Number(form.quiz_default_questions_per_module),
            quiz_default_pass_score:
                form.quiz_default_pass_score === "" ? null : Math.max(0, Math.min(1, Number(form.quiz_default_pass_score) / 100)),
            quiz_default_max_retakes:
                form.quiz_default_max_retakes === "" ? null : Number(form.quiz_default_max_retakes),
            has_final_exam: !!form.has_final_exam,
            final_exam_question_count:
                form.final_exam_question_count === "" ? null : Number(form.final_exam_question_count),
            final_exam_pass_score:
                form.final_exam_pass_score === "" ? null : Math.max(0, Math.min(1, Number(form.final_exam_pass_score) / 100)),
            publish_at: form.publish_at ? new Date(form.publish_at).toISOString() : null,
            expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
            // Section 4
            prerequisite_course_ids: form.prerequisite_course_ids || [],
            enrollment_type: form.enrollment_type || "open",
            // Section 5 — Pricing (author-editable; INR for v1)
            is_free: !!form.is_free,
            price_amount_cents: form.is_free ? 0 : (
                form.price_amount_rupees === "" || form.price_amount_rupees == null
                    ? null
                    : Math.max(0, Math.round(Number(form.price_amount_rupees) * 100))
            ),
            currency: "INR",
            ce_credits: form.ce_credits === "" ? null : Number(form.ce_credits),
            ce_authority: form.ce_authority || null,
            // Section 7 — save-only
            case_validation_required: !!form.case_validation_required,
            case_validation_notes: form.case_validation_notes || "",
            // iter100.156g — Admin-curated Recommended pin (server-side
            // silently strips this if a non-admin sends it).
            is_featured: !!form.is_featured,
        };
        return p;
    };

    const autoSave = async () => {
        if (!isEdit || !canEdit || !form.title.trim() || form.title.trim().length < 3) return;
        try {
            setSaving(true);
            await api.put(`/course-builder/courses/${courseId}`, buildPayload());
            setLastSavedAt(new Date());
            dirtyRef.current = false;
        } catch {
            // silent on auto-save; explicit save has toasts
        } finally {
            setSaving(false);
        }
    };

    const onManualSave = async (goToBuilder) => {
        if (!form.title.trim() || form.title.trim().length < 3) {
            toast.error("Give the course a title (min 3 characters).");
            setOpenSections({ basic: true });
            setActiveSection("basic");
            return;
        }
        try {
            setSaving(true);
            let id = courseId;
            if (isEdit) {
                await api.put(`/course-builder/courses/${courseId}`, buildPayload());
                setLastSavedAt(new Date());
                dirtyRef.current = false;
                toast.success("Setup saved");
            } else {
                const { data } = await api.post("/course-builder/courses", buildPayload());
                id = data.id;
                toast.success("Course created");
            }
            if (id && (goToBuilder || !isEdit)) navigate(`/creator/courses/${id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save course.");
        } finally {
            setSaving(false);
        }
    };

    // ─── Completion checklist ─────────────────────────────────────────
    const checklist = useMemo(() => ([
        { key: "title",         label: "Course Name",         done: !!form.title.trim() },
        { key: "type",          label: "Course Type",         done: !!form.course_type },
        { key: "tracks",        label: "Learning Track",      done: (form.learning_tracks || []).length > 0 },
        { key: "description",   label: "Description",         done: !!(form.description || "").replace(/<[^>]*>/g, "").trim() },
        { key: "phase",         label: "Phase",               done: !!form.phase },
        { key: "duration",      label: "Duration",            done: (form.duration_minutes !== "" && form.duration_minutes !== null) || computedDurationMinutes > 0 },
        { key: "enrollment",    label: "Enrollment type",     done: !!form.enrollment_type },
        { key: "quiz_type",     label: "Quiz defaults",       done: !!form.quiz_default_type && form.quiz_default_pass_score !== "" },
        { key: "pricing",       label: "Pricing & CE",        done: form.is_free === true || (form.price_amount_rupees !== "" && form.price_amount_rupees !== null && Number(form.price_amount_rupees) > 0) },
        { key: "publish",       label: "Publishing status",   done: true },  // status is workflow-driven
    ]), [form, computedDurationMinutes]);

    const filledCount = checklist.filter((c) => c.done).length;
    const totalCount = checklist.length;
    const progressPct = Math.round((filledCount / totalCount) * 100);

    if (loading) {
        return (
            <div className="p-8 max-w-6xl mx-auto space-y-4">
                <div className="h-8 w-56 bg-secondary rounded animate-pulse" />
                <div className="h-96 bg-secondary rounded-2xl animate-pulse" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-secondary/30 lg:pl-6" data-testid="course-setup">
            {/* Topbar */}
            <header className="bg-card border-b border-border sticky top-0 z-20" data-testid="course-setup-topbar">
                <div className="px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                        <Link
                            to="/creator"
                            className="w-8 h-8 grid place-items-center rounded-md hover:bg-secondary transition-colors text-muted-foreground no-underline"
                            data-testid="setup-back-link"
                            title="Back to My Courses"
                        >
                            <ArrowLeft size={14} weight="bold" />
                        </Link>
                        <h1 className="font-display text-lg font-bold tracking-tight truncate max-w-md" data-testid="setup-title-header">
                            {isEdit ? (form.title || "Untitled Course") : "Create New Course"}
                        </h1>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${
                            status === "draft"        ? "bg-slate-100 text-slate-700 border-slate-200" :
                            status === "under_review" ? "bg-amber-50 text-amber-800 border-amber-200"    :
                            status === "published"    ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                                                       "bg-rose-50 text-rose-800 border-rose-200"
                        }`}>
                            {status.replace("_", " ")}
                        </span>
                        {saving && (
                            <span className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="setup-saving-indicator">
                                <CircleDashed size={11} className="animate-spin" /> Saving…
                            </span>
                        )}
                        {!saving && lastSavedAt && (
                            <span className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="setup-saved-indicator">
                                <CheckCircle size={11} weight="fill" className="text-emerald-500" /> Saved
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {isEdit && (
                            <Link
                                to={`/creator/courses/${courseId}`}
                                className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-border bg-secondary text-xs font-semibold no-underline hover:bg-secondary/60 transition-colors"
                                data-testid="setup-open-builder-btn"
                            >
                                Open Builder
                            </Link>
                        )}
                        <Button
                            onClick={() => onManualSave(false)}
                            disabled={saving || !canEdit || !form.title.trim()}
                            data-testid="setup-save-btn"
                        >
                            <FloppyDisk size={13} weight="fill" className="mr-1.5" />
                            {isEdit ? "Save changes" : "Save & Continue"}
                        </Button>
                    </div>
                </div>
            </header>

            {/* 3-column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_300px] min-h-[calc(100vh-3.5rem)]">
                {/* Left rail — section nav */}
                <aside className="hidden lg:block bg-card border-r border-border sticky top-14 self-start max-h-[calc(100vh-3.5rem)] overflow-y-auto" data-testid="setup-section-nav">
                    <div className="px-4 pt-5 pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Sections</div>
                    {SECTIONS.map((s, idx) => {
                        const active = activeSection === s.key;
                        // "done" = sectiony-required fields filled (simplified: at least one field of that section has value)
                        const sectionDone = isSectionDone(s.key, form, checklist);
                        return (
                            <button
                                key={s.key}
                                type="button"
                                onClick={() => {
                                    setActiveSection(s.key);
                                    setOpenSections({ [s.key]: true });
                                    document.getElementById(`section-${s.key}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm border-l-2 transition-colors ${
                                    active
                                        ? "bg-primary/5 text-primary border-primary font-semibold"
                                        : "border-transparent text-muted-foreground hover:bg-secondary/40 hover:text-foreground"
                                }`}
                                data-testid={`section-nav-${s.key}`}
                            >
                                <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold shrink-0 ${
                                    sectionDone ? "bg-emerald-500 text-white"
                                              : active ? "bg-primary text-primary-foreground"
                                                       : "bg-secondary text-muted-foreground"
                                }`}>
                                    {sectionDone ? <CheckCircle size={10} weight="fill" /> : idx + 1}
                                </span>
                                {s.title}
                            </button>
                        );
                    })}
                    <div className="mt-2 mx-4 mb-3 mt-6 p-3 rounded-lg bg-primary/5 border border-dashed border-primary/20">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-primary">New in this release</div>
                        <div className="mt-1 text-[11px] text-muted-foreground leading-relaxed">
                            Access &amp; Eligibility, Pricing (Free / Paid), and Case Validation are all editable below.
                        </div>
                    </div>
                </aside>

                {/* Main content */}
                <main className="p-6 md:p-8 min-w-0">
                    {/* Progress bar */}
                    <div className="mb-6" data-testid="setup-progress">
                        <div className="flex items-center justify-between text-xs mb-2">
                            <span className="font-semibold text-muted-foreground">Course Completion</span>
                            <span className="text-muted-foreground tabular-nums">{filledCount} of {totalCount} — {progressPct}%</span>
                        </div>
                        <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-500"
                                style={{ width: `${progressPct}%` }}
                            />
                        </div>
                    </div>

                    {!canEdit && (
                        <div className="mb-4 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center gap-2 text-xs text-amber-900" data-testid="setup-readonly-banner">
                            <Info size={13} weight="fill" className="text-amber-600 shrink-0" />
                            <span className="font-semibold">
                                {status === "under_review"
                                    ? "This course is under admin review — editing is disabled until a decision."
                                    : "This course is published — ask an admin to unpublish to make changes."}
                            </span>
                        </div>
                    )}

                    <fieldset disabled={!canEdit} className="space-y-4">
                        {/* Section 1: Basic */}
                        <SectionCard
                            id="basic"
                            openState={openSections}
                            setOpenState={setOpenSections}
                            setActiveSection={setActiveSection}
                            section={SECTIONS[0]}
                            tone="sky"
                        >
                            <FieldRow>
                                <Field label="Course Name" required span={2}>
                                    <Input
                                        value={form.title}
                                        onChange={(e) => set({ title: e.target.value })}
                                        placeholder="e.g. Clear Aligner Fundamentals — General Dentist Track"
                                        maxLength={140}
                                        data-testid="setup-name"
                                    />
                                    <Hint>This is the name doctors will see on their course dashboard.</Hint>
                                </Field>
                            </FieldRow>
                            <FieldRow>
                                <Field label="Course Type" required>
                                    <select
                                        value={form.course_type}
                                        onChange={(e) => set({ course_type: e.target.value })}
                                        className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                                        data-testid="setup-course-type"
                                    >
                                        <option value="">— Select Course Type —</option>
                                        {COURSE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                                    </select>
                                    {form.course_type && (
                                        <Hint>{COURSE_TYPES.find((t) => t.value === form.course_type)?.desc}</Hint>
                                    )}
                                </Field>
                                <Field label="Learning Track" required>
                                    <div className="flex flex-wrap gap-1.5" data-testid="setup-learning-tracks">
                                        {LEARNING_TRACKS.map((t) => {
                                            const on = form.learning_tracks.includes(t.value);
                                            return (
                                                <button
                                                    key={t.value}
                                                    type="button"
                                                    onClick={() => toggleTrack(t.value)}
                                                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-colors ${
                                                        on
                                                            ? "bg-primary/10 border-primary text-primary"
                                                            : "bg-background border-border text-muted-foreground hover:border-primary/40 hover:text-primary"
                                                    }`}
                                                    data-testid={`setup-track-${t.value}`}
                                                >
                                                    <t.Icon size={11} weight={on ? "fill" : "duotone"} />
                                                    {t.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </Field>
                            </FieldRow>
                            <FieldRow>
                                <Field label="Course Description" required span={2}>
                                    <RichTextEditor
                                        value={form.description}
                                        onChange={(html) => set({ description: html })}
                                        disabled={!canEdit}
                                    />
                                </Field>
                            </FieldRow>
                            <FieldRow>
                                <Field label="Thumbnail" span={2}>
                                    <ThumbnailUploader
                                        value={form.thumbnail_media_id}
                                        onChange={(id) => set({ thumbnail_media_id: id })}
                                        courseId={courseId}
                                    />
                                </Field>
                            </FieldRow>
                        </SectionCard>

                        {/* Section 2: Structure */}
                        <SectionCard
                            id="structure"
                            openState={openSections}
                            setOpenState={setOpenSections}
                            setActiveSection={setActiveSection}
                            section={SECTIONS[1]}
                            tone="violet"
                        >
                            {(() => {
                                const hint = COURSE_TYPE_PHASE_HINTS[form.course_type];
                                if (!form.course_type || !form.phase || !hint) return null;
                                if (hint.phases.includes(form.phase)) return null;
                                const typeLabel = COURSE_TYPES.find((t) => t.value === form.course_type)?.label || form.course_type;
                                return (
                                    <div
                                        className="mb-3 flex items-start gap-2 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 leading-snug"
                                        data-testid="setup-phase-mismatch-warn"
                                    >
                                        <Warning size={13} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
                                        <div>
                                            <span className="font-bold">Unusual pairing:</span> {typeLabel} courses typically sit in <span className="font-semibold">{hint.label}</span>. You can still save this combo — just double-check it&apos;s intentional.
                                        </div>
                                    </div>
                                );
                            })()}
                            <FieldRow>
                                <Field label="Phase" required>
                                    <select
                                        value={form.phase}
                                        onChange={(e) => set({ phase: e.target.value })}
                                        className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                                        data-testid="setup-phase"
                                    >
                                        <option value="">— Select Phase —</option>
                                        {PHASES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                                    </select>
                                    {form.phase && <Hint>{PHASES.find((p) => p.value === form.phase)?.desc}</Hint>}
                                </Field>
                                <Field label="Estimated Duration (hours)">
                                    <Input
                                        type="number"
                                        min="0" max="200" step="0.5"
                                        value={form.duration_minutes === "" ? "" : form.duration_minutes / 60}
                                        onChange={(e) => {
                                            const hrs = e.target.value === "" ? "" : Number(e.target.value);
                                            set({ duration_minutes: hrs === "" ? "" : Math.round(hrs * 60) });
                                        }}
                                        placeholder={computedDurationMinutes > 0 ? `${(computedDurationMinutes / 60).toFixed(1)} (auto)` : "6"}
                                        data-testid="setup-duration"
                                    />
                                    {form.duration_minutes === "" && computedDurationMinutes > 0 ? (
                                        <Hint>Auto-computed from lesson durations: <b>{(computedDurationMinutes / 60).toFixed(1)} hrs</b> ({computedDurationMinutes} min). Leave blank to keep auto — type a value to override.</Hint>
                                    ) : form.duration_minutes !== "" && computedDurationMinutes > 0 && Number(form.duration_minutes) !== computedDurationMinutes ? (
                                        <Hint>Manual override. Lessons currently sum to {(computedDurationMinutes / 60).toFixed(1)} hrs — clear the field to use the auto value.</Hint>
                                    ) : (
                                        <Hint>Enter a target (e.g. 6 hrs) or set per-lesson durations in the Builder to auto-compute.</Hint>
                                    )}
                                </Field>
                            </FieldRow>

                            <FieldRow>
                                <Field label="Content Format (auto-derived)" span={2}>
                                    <div className="flex flex-wrap gap-1.5" data-testid="setup-content-format">
                                        {derivedContentFormat.length === 0 ? (
                                            <div className="text-[11px] text-muted-foreground italic px-2 py-1">
                                                Format chips appear here as you add lessons in the Builder.
                                            </div>
                                        ) : derivedContentFormat.map(([type, count]) => {
                                            const meta = LESSON_TYPE_META[type];
                                            return (
                                                <span key={type} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20 text-[11px] font-semibold">
                                                    <meta.Icon size={11} weight="fill" />
                                                    {count} {meta.label}{count === 1 ? "" : "s"}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </Field>
                            </FieldRow>

                            <FieldRow>
                                <Field label="Content Tone">
                                    <select
                                        value={form.content_tone}
                                        onChange={(e) => set({ content_tone: e.target.value })}
                                        className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                                        data-testid="setup-content-tone"
                                    >
                                        <option value="">— Select Tone —</option>
                                        {CONTENT_TONES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </select>
                                </Field>
                                <Field label="Total Modules (auto)">
                                    <Input
                                        type="number"
                                        value={modules.length}
                                        disabled
                                        className="bg-secondary/40"
                                        data-testid="setup-total-modules"
                                    />
                                    <Hint>Populated from your Builder — add modules there.</Hint>
                                </Field>
                            </FieldRow>
                        </SectionCard>

                        {/* Section 3: Access & Eligibility */}
                        <SectionCard
                            id="access"
                            openState={openSections}
                            setOpenState={setOpenSections}
                            setActiveSection={setActiveSection}
                            section={SECTIONS[2]}
                            tone="teal"
                        >
                            <FieldRow>
                                <Field label="Enrollment Type" span={2}>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="setup-enrollment-type">
                                        {[
                                            { value: "open",        label: "Open",        desc: "Any doctor can self-enroll", tone: "emerald" },
                                            { value: "application", label: "Application", desc: "Doctor applies · admin approves", tone: "amber" },
                                            { value: "invitation",  label: "Invitation",  desc: "Admins invite specific doctors",  tone: "rose" },
                                        ].map((et) => {
                                            const on = form.enrollment_type === et.value;
                                            const toneClass = on ? (et.tone === "emerald" ? "border-emerald-500 bg-emerald-50" :
                                                                    et.tone === "amber"   ? "border-amber-500 bg-amber-50" :
                                                                                            "border-rose-500 bg-rose-50")
                                                                : "border-border bg-background";
                                            return (
                                                <button
                                                    key={et.value}
                                                    type="button"
                                                    onClick={() => set({ enrollment_type: et.value })}
                                                    className={`text-left px-3 py-2.5 rounded-lg border-2 transition-colors ${toneClass}`}
                                                    data-testid={`setup-enrollment-${et.value}`}
                                                >
                                                    <div className="text-xs font-bold">{et.label}</div>
                                                    <div className="text-[11px] text-muted-foreground mt-0.5">{et.desc}</div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <Hint>Application &amp; Invitation flows will land in a future release — MVP hard-gates them as unavailable to learners.</Hint>
                                </Field>
                            </FieldRow>

                            <FieldRow>
                                <Field label="Prerequisite Courses" span={2}>
                                    {availableCourses.length === 0 ? (
                                        <div className="text-[11px] text-muted-foreground italic px-2 py-2 border border-dashed border-border rounded-lg" data-testid="setup-prereqs-empty">
                                            You haven&apos;t authored any other courses yet — prerequisites will appear once you have more courses.
                                        </div>
                                    ) : (
                                        <div className="space-y-1.5" data-testid="setup-prerequisites">
                                            {availableCourses.map((c) => {
                                                const on = (form.prerequisite_course_ids || []).includes(c.id);
                                                return (
                                                    <label
                                                        key={c.id}
                                                        className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${on ? "bg-primary/5 border-primary/40" : "border-border hover:bg-secondary/40"}`}
                                                        data-testid={`setup-prereq-${c.id}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={on}
                                                            onChange={() => {
                                                                const cur = form.prerequisite_course_ids || [];
                                                                set({
                                                                    prerequisite_course_ids: on
                                                                        ? cur.filter((x) => x !== c.id)
                                                                        : [...cur, c.id],
                                                                });
                                                            }}
                                                            className="h-4 w-4 shrink-0"
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-xs font-semibold truncate">{c.title}</div>
                                                            <div className="text-[10px] text-muted-foreground">
                                                                {c.status} · {c.total_modules || 0} modules
                                                            </div>
                                                        </div>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <Hint>Doctors won&apos;t be able to enroll until they&apos;ve earned certificates in every selected prerequisite.</Hint>
                                </Field>
                            </FieldRow>
                        </SectionCard>

                        {/* Section 4: Assessment */}
                        <SectionCard
                            id="quiz"
                            openState={openSections}
                            setOpenState={setOpenSections}
                            setActiveSection={setActiveSection}
                            section={SECTIONS[3]}
                            tone="amber"
                        >
                            <FieldRow>
                                <Field label="Quiz Type (default)" required>
                                    <select
                                        value={form.quiz_default_type}
                                        onChange={(e) => set({ quiz_default_type: e.target.value })}
                                        className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                                        data-testid="setup-quiz-type"
                                    >
                                        <option value="">— Select Quiz Type —</option>
                                        {QUIZ_TYPES.map((q) => <option key={q.value} value={q.value}>{q.label}</option>)}
                                    </select>
                                </Field>
                                <Field label="Questions per Module" required>
                                    <Input
                                        type="number"
                                        min="1" max="50"
                                        value={form.quiz_default_questions_per_module}
                                        onChange={(e) => set({ quiz_default_questions_per_module: e.target.value })}
                                        placeholder="6"
                                        data-testid="setup-questions-per-module"
                                    />
                                    <Hint>Recommended: 5–8 questions per module quiz.</Hint>
                                </Field>
                            </FieldRow>
                            <FieldRow>
                                <Field label="Passing Score (%)" required>
                                    <Input
                                        type="number"
                                        min="0" max="100" step="5"
                                        value={form.quiz_default_pass_score}
                                        onChange={(e) => set({ quiz_default_pass_score: e.target.value })}
                                        placeholder="70"
                                        data-testid="setup-pass-score"
                                    />
                                </Field>
                                <Field label="Max Retake Attempts">
                                    <Input
                                        type="number"
                                        min="0" max="20"
                                        value={form.quiz_default_max_retakes}
                                        onChange={(e) => set({ quiz_default_max_retakes: e.target.value })}
                                        placeholder="3"
                                        data-testid="setup-max-retakes"
                                    />
                                    <Hint>Leave blank for unlimited retakes.</Hint>
                                </Field>
                            </FieldRow>

                            <ToggleRow
                                label="Final Exam Included?"
                                desc="Adds a course-level capstone exam separate from module quizzes."
                                checked={form.has_final_exam}
                                onChange={(v) => set({ has_final_exam: v })}
                                testid="setup-final-exam-toggle"
                            />

                            {form.has_final_exam && (
                                <FieldRow>
                                    <Field label="Final Exam Question Count">
                                        <Input
                                            type="number"
                                            min="1" max="200"
                                            value={form.final_exam_question_count}
                                            onChange={(e) => set({ final_exam_question_count: e.target.value })}
                                            placeholder="35"
                                            data-testid="setup-final-exam-count"
                                        />
                                        <Hint>How many questions the doctor sees in the exam. In Curated mode you must author at least this many; in Auto-sample mode the server picks this many at random.</Hint>
                                    </Field>
                                    <Field label="Final Exam Pass Score (%)">
                                        <Input
                                            type="number"
                                            min="0" max="100" step="5"
                                            value={form.final_exam_pass_score}
                                            onChange={(e) => set({ final_exam_pass_score: e.target.value })}
                                            placeholder="70"
                                            data-testid="setup-final-exam-pass"
                                        />
                                        <Hint>Doctors must meet this score in the final exam to earn the certificate.</Hint>
                                    </Field>
                                </FieldRow>
                            )}

                            {form.has_final_exam && (
                                <div className="mt-2 flex items-start gap-2 text-[11px] text-primary bg-primary/5 border border-primary/20 rounded-lg px-3 py-2 leading-snug" data-testid="setup-final-exam-builder-hint">
                                    <Info size={12} weight="fill" className="text-primary shrink-0 mt-0.5" />
                                    <div>
                                        <span className="font-bold">Write the exam questions in the Course Builder.</span> Open the Builder{isEdit ? "" : " (after you save this course)"} and click <b>Final Exam</b> at the bottom of the module list — you can author questions there or switch to auto-sample from your module quizzes.
                                    </div>
                                </div>
                            )}
                        </SectionCard>

                        {/* Section 5: Pricing & CE Credits */}
                        <SectionCard
                            id="pricing"
                            openState={openSections}
                            setOpenState={setOpenSections}
                            setActiveSection={setActiveSection}
                            section={SECTIONS[4]}
                            tone="sky"
                        >
                            <div className="mb-3">
                                <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">
                                    Pricing Model
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => set({ is_free: true, price_amount_rupees: "" })}
                                        className={`text-left rounded-lg border p-3 transition-colors ${
                                            form.is_free
                                                ? "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-200"
                                                : "border-border bg-card hover:border-emerald-200 hover:bg-emerald-50/40"
                                        }`}
                                        data-testid="setup-pricing-free"
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className={`w-3.5 h-3.5 rounded-full border-2 ${form.is_free ? "bg-emerald-500 border-emerald-500" : "border-muted-foreground/40"}`} />
                                            <span className="font-semibold text-sm">Free</span>
                                        </div>
                                        <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
                                            Any eligible doctor can enroll instantly.
                                        </p>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => set({ is_free: false })}
                                        className={`text-left rounded-lg border p-3 transition-colors ${
                                            !form.is_free
                                                ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                                                : "border-border bg-card hover:border-primary/40 hover:bg-primary/5"
                                        }`}
                                        data-testid="setup-pricing-paid"
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className={`w-3.5 h-3.5 rounded-full border-2 ${!form.is_free ? "bg-primary border-primary" : "border-muted-foreground/40"}`} />
                                            <span className="font-semibold text-sm">Paid — One-time purchase</span>
                                        </div>
                                        <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
                                            Doctor pays once for lifetime access.
                                        </p>
                                    </button>
                                </div>
                            </div>

                            {!form.is_free && (
                                <div className="mb-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block">
                                            <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Price (₹ INR)</span>
                                            <div className="mt-1 relative">
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground pointer-events-none">₹</span>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    max="100000"
                                                    step="1"
                                                    value={form.price_amount_rupees}
                                                    onChange={(e) => set({ price_amount_rupees: e.target.value })}
                                                    placeholder="4999"
                                                    className="pl-7"
                                                    data-testid="setup-price-input"
                                                />
                                            </div>
                                            <Hint>Doctors are charged in Indian Rupees. Max ₹1,00,000.</Hint>
                                        </label>
                                    </div>
                                    <div>
                                        <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Preview</span>
                                        <div
                                            className="mt-1 rounded-lg border border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10 px-4 py-3"
                                            data-testid="setup-price-preview"
                                        >
                                            <div className="text-[9px] font-bold uppercase tracking-wider text-primary/70">Doctors will see</div>
                                            <div className="mt-0.5 flex items-baseline gap-1.5">
                                                <span className="font-display text-2xl font-black text-primary tabular-nums">
                                                    ₹{Number(form.price_amount_rupees || 0).toLocaleString("en-IN")}
                                                </span>
                                                <span className="text-[11px] font-semibold text-muted-foreground">· One-time</span>
                                            </div>
                                            <div className="text-[10.5px] text-muted-foreground mt-0.5">Lifetime access · Certificate on completion</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!form.is_free && (
                                <div className="mb-3 flex items-start gap-2 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 leading-snug" data-testid="setup-price-checkout-notice">
                                    <Warning size={13} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
                                    <div>
                                        <span className="font-bold">Checkout gateway pending.</span> The price is saved and doctors will see it on the catalogue, but a payment provider (Stripe / Razorpay) hasn&apos;t been wired yet. Until it ships, doctors clicking &ldquo;Enroll&rdquo; on this course will see a Contact-CoE prompt so your team can onboard them manually.
                                    </div>
                                </div>
                            )}

                            <FieldRow>
                                <Field label="CE Credits (Continuing Education)">
                                    <Input
                                        type="number"
                                        min="0" max="100" step="0.25"
                                        value={form.ce_credits}
                                        onChange={(e) => set({ ce_credits: e.target.value })}
                                        placeholder="e.g. 6"
                                        data-testid="setup-ce-credits"
                                    />
                                    <Hint>Displayed on the doctor&apos;s certificate.</Hint>
                                </Field>
                                <Field label="CE Authority / Regulator">
                                    <Input
                                        value={form.ce_authority}
                                        onChange={(e) => set({ ce_authority: e.target.value })}
                                        placeholder="e.g. AGD PACE · DCI CDE"
                                        maxLength={100}
                                        data-testid="setup-ce-authority"
                                    />
                                    <Hint>Free-form text — printed alongside CE credits.</Hint>
                                </Field>
                            </FieldRow>
                        </SectionCard>

                        {/* Section 6: Case Validation (save-only for MVP) */}
                        <SectionCard
                            id="validation"
                            openState={openSections}
                            setOpenState={setOpenSections}
                            setActiveSection={setActiveSection}
                            section={SECTIONS[5]}
                            tone="rose"
                        >
                            <ToggleRow
                                label="Require case submission for certification"
                                desc="Doctor uploads a real patient case for CoE clinical review before the certificate is issued."
                                checked={form.case_validation_required}
                                onChange={(v) => set({ case_validation_required: v })}
                                testid="setup-case-validation-toggle"
                            />
                            {form.case_validation_required && (
                                <FieldRow>
                                    <Field label="Case Validation Instructions" span={2}>
                                        <RichTextEditor
                                            value={form.case_validation_notes}
                                            onChange={(html) => set({ case_validation_notes: html })}
                                            placeholder="What should the doctor submit? e.g. 1 pre-op scan + 1 post-op scan + treatment plan summary."
                                            disabled={!canEdit}
                                            size="sm"
                                        />
                                        <input type="hidden" data-testid="setup-case-validation-notes" value={form.case_validation_notes || ""} readOnly />
                                        <Hint>Shown on the doctor&apos;s Case Submission drawer. Once the doctor uploads a case, admins review it under <strong>Admin → Case Validations</strong>. The certificate is only issued after approval.</Hint>
                                    </Field>
                                </FieldRow>
                            )}
                            <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mt-2" data-testid="setup-case-validation-note">
                                <CheckCircle size={11} weight="fill" className="inline mr-1 -mt-0.5" />
                                Enforced end-to-end: doctors submit under &ldquo;Case Submission&rdquo; on the course viewer; admins review at <strong>Admin → Case Validations</strong>; the certificate unlocks only after approval.
                            </div>
                        </SectionCard>

                        {/* Section 7: Publishing */}
                        <SectionCard
                            id="publish"
                            openState={openSections}
                            setOpenState={setOpenSections}
                            setActiveSection={setActiveSection}
                            section={SECTIONS[6]}
                            tone="teal"
                        >
                            <FieldRow>
                                <Field label="Current status">
                                    <div className="h-10 flex items-center px-3 rounded-md border border-input bg-secondary/40 text-sm font-semibold" data-testid="setup-status-display">
                                        {status === "draft"        && <span className="inline-flex items-center gap-1"><PencilSimple size={12} /> Draft — not yet submitted</span>}
                                        {status === "under_review" && <span className="inline-flex items-center gap-1 text-amber-800"><ClockCounterClockwise size={12} /> Under admin review</span>}
                                        {status === "published"    && <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={12} weight="fill" /> Published</span>}
                                        {status === "rejected"     && <span className="inline-flex items-center gap-1 text-rose-700"><Warning size={12} weight="fill" /> Rejected — awaiting revisions</span>}
                                    </div>
                                    <Hint>Status is controlled by the admin review workflow (Submit for Review from the Builder).</Hint>
                                </Field>
                                <Field label="Publish Date">
                                    <Input
                                        type="date"
                                        value={form.publish_at}
                                        onChange={(e) => set({ publish_at: e.target.value })}
                                        data-testid="setup-publish-at"
                                    />
                                    <Hint>Leave blank to publish immediately once approved.</Hint>
                                </Field>
                            </FieldRow>
                            <FieldRow>
                                <Field label="Expiry Date">
                                    <Input
                                        type="date"
                                        value={form.expires_at}
                                        onChange={(e) => set({ expires_at: e.target.value })}
                                        data-testid="setup-expires-at"
                                    />
                                    <Hint>Optional — for time-limited courses. Doctors won&apos;t see this course after this date.</Hint>
                                </Field>
                            </FieldRow>
                            {isAdmin && (
                                <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-rose-200 bg-rose-50/50 hover:bg-rose-50 mt-3">
                                    <input
                                        type="checkbox"
                                        checked={!!form.is_featured}
                                        onChange={(e) => set({ is_featured: e.target.checked })}
                                        disabled={!canEdit}
                                        data-testid="setup-is-featured"
                                        className="mt-0.5"
                                    />
                                    <div>
                                        <div className="text-sm font-semibold flex items-center gap-1">
                                            <Sparkle size={12} weight="fill" className="text-rose-500" />
                                            Feature on doctor&apos;s &ldquo;Recommended&rdquo; tab
                                        </div>
                                        <div className="text-[11px] text-muted-foreground">
                                            Pins this course to the top of the Learning Academy &rarr; Recommended tab. Admin-only control.
                                        </div>
                                    </div>
                                </label>
                            )}
                        </SectionCard>

                        {/* Submit CTA at the bottom */}
                        <div className="flex items-center justify-between gap-4 flex-wrap pt-4">
                            <div className="text-xs text-muted-foreground">
                                {isEdit
                                    ? "Changes auto-save 2 seconds after you stop editing."
                                    : "Save & continue to add modules and lessons in the Builder."}
                            </div>
                            <div className="flex items-center gap-2">
                                {isEdit && (
                                    <Link
                                        to={`/creator/courses/${courseId}`}
                                        className="inline-flex items-center gap-1 h-9 px-4 rounded-md border border-border bg-secondary text-sm font-semibold no-underline hover:bg-secondary/60 transition-colors"
                                        data-testid="setup-open-builder-bottom"
                                    >
                                        Open Builder <PaperPlaneTilt size={12} weight="fill" />
                                    </Link>
                                )}
                                <Button
                                    onClick={() => onManualSave(true)}
                                    disabled={saving || !canEdit || !form.title.trim()}
                                    data-testid="setup-save-and-continue-btn"
                                >
                                    <FloppyDisk size={13} weight="fill" className="mr-1.5" />
                                    {isEdit ? "Save changes" : "Save & Continue"}
                                </Button>
                            </div>
                        </div>
                    </fieldset>
                </main>

                {/* Right rail — summary + checklist */}
                <aside className="hidden lg:block bg-card border-l border-border sticky top-14 self-start max-h-[calc(100vh-3.5rem)] overflow-y-auto p-5" data-testid="setup-summary-rail">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-3">Course Summary</div>
                    <SummaryItem k="Course Name"    v={form.title || null} />
                    <SummaryItem k="Course Type"    v={COURSE_TYPES.find((t) => t.value === form.course_type)?.label} />
                    <SummaryItem k="Learning Track" v={form.learning_tracks.length ? LEARNING_TRACKS.filter((t) => form.learning_tracks.includes(t.value)).map((t) => t.label).join(", ") : null} />
                    <SummaryItem k="Phase"          v={PHASES.find((p) => p.value === form.phase)?.label} />
                    <SummaryItem k="Duration"       v={displayDurationMinutes ? `${(displayDurationMinutes / 60).toFixed(1)} hrs${form.duration_minutes === "" && computedDurationMinutes > 0 ? " (auto)" : ""}` : null} />
                    <SummaryItem k="Pass Score"     v={form.quiz_default_pass_score ? `${form.quiz_default_pass_score}%` : null} />
                    <SummaryItem k="Final Exam"     v={form.has_final_exam ? (form.final_exam_question_count ? `${form.final_exam_question_count} Qs` : "Yes") : null} />
                    <SummaryItem k="Status"         v={status.replace("_", " ")} />

                    <div className="h-px bg-border my-4" />

                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-3">Completion Checklist</div>
                    {checklist.map((c) => (
                        <div key={c.key} className="flex items-center gap-2 mb-1.5 text-xs" data-testid={`checklist-${c.key}`}>
                            {c.done
                                ? <CheckCircle size={13} weight="fill" className="text-emerald-500 shrink-0" />
                                : <Circle size={13} weight="regular" className="text-muted-foreground/40 shrink-0" />}
                            <span className={c.done ? "text-foreground" : "text-muted-foreground"}>{c.label}</span>
                        </div>
                    ))}

                    {isEdit && courseId && (
                        <>
                            <div className="h-px bg-border my-4" />
                            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-3 flex items-center gap-1.5">
                                <ClockCounterClockwise size={11} weight="duotone" className="text-primary" />
                                Activity Monitor
                            </div>
                            <div className="max-h-[420px] overflow-y-auto pr-1 -mr-1" data-testid="setup-activity-scroll">
                                <CourseActivityFeed
                                    courseId={courseId}
                                    compact
                                    testid="setup-activity-feed"
                                />
                            </div>
                        </>
                    )}
                </aside>
            </div>
        </div>
    );
}


// ─── Sub-components ────────────────────────────────────────────────────

const TONE_CLS = {
    sky:    "bg-sky-50 text-sky-700",
    violet: "bg-violet-50 text-violet-700",
    amber:  "bg-amber-50 text-amber-700",
    teal:   "bg-primary/10 text-primary",
};

function SectionCard({ id, section, tone, openState, setOpenState, setActiveSection, children }) {
    const open = !!openState[id];
    const Icon = section.Icon;
    return (
        <div
            id={`section-${id}`}
            className="bg-card border border-border rounded-2xl overflow-hidden transition-shadow hover:shadow-sm"
            data-testid={`section-card-${id}`}
        >
            <button
                type="button"
                onClick={() => {
                    // Single-accordion behaviour — clicking a section closes
                    // any other open section so only one panel is visible at a time.
                    setOpenState((s) => (s[id] ? {} : { [id]: true }));
                    setActiveSection(id);
                }}
                className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-secondary/30 transition-colors"
                data-testid={`section-toggle-${id}`}
            >
                <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl grid place-items-center ${TONE_CLS[tone] || "bg-secondary text-muted-foreground"}`}>
                        <Icon size={16} weight="duotone" />
                    </div>
                    <div className="text-left">
                        <div className="text-sm font-bold">{section.title}</div>
                        <div className="text-[11px] text-muted-foreground">{section.subtitle}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                        section.required
                            ? "bg-rose-50 text-rose-700 border border-rose-200"
                            : "bg-secondary text-muted-foreground border border-border"
                    }`}>
                        {section.required ? "Required" : "Optional"}
                    </span>
                    <CaretDown size={14} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
                </div>
            </button>
            <div className={`${open ? "" : "hidden"} px-4 pb-5 pt-1 space-y-4`}>{children}</div>
        </div>
    );
}


function FieldRow({ children }) {
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}


function Field({ label, required, span, children }) {
    return (
        <div className={span === 2 ? "md:col-span-2" : ""}>
            <Label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground flex items-center gap-1">
                {label} {required && <span className="text-rose-600">*</span>}
            </Label>
            <div className="mt-1.5 space-y-1">{children}</div>
        </div>
    );
}


function Hint({ children }) {
    return <p className="text-[11px] text-muted-foreground">{children}</p>;
}


function ToggleRow({ label, desc, checked, onChange, testid }) {
    return (
        <label className="flex items-center justify-between gap-4 p-3 rounded-xl border border-border bg-secondary/30 cursor-pointer" data-testid={testid}>
            <div>
                <div className="text-sm font-semibold">{label}</div>
                {desc && <div className="text-[11px] text-muted-foreground mt-0.5">{desc}</div>}
            </div>
            <div className="relative shrink-0">
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                    className="sr-only peer"
                />
                <div className="w-10 h-6 bg-secondary rounded-full peer-checked:bg-primary transition-colors border border-border" />
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-4" : ""}`} />
            </div>
        </label>
    );
}


function SummaryItem({ k, v }) {
    return (
        <div className="mb-2 p-2.5 rounded-lg bg-secondary/40 border border-border" data-testid={`summary-${k.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{k}</div>
            <div className={`text-xs font-semibold mt-0.5 ${v ? "text-foreground" : "text-muted-foreground/50 italic"}`}>
                {v || "Not set"}
            </div>
        </div>
    );
}


// ─── Helpers ───────────────────────────────────────────────────────────

function isSectionDone(key, form, checklist) {
    if (key === "basic")      return checklist.find((c) => c.key === "title")?.done && checklist.find((c) => c.key === "type")?.done && checklist.find((c) => c.key === "tracks")?.done;
    if (key === "structure")  return checklist.find((c) => c.key === "phase")?.done && checklist.find((c) => c.key === "duration")?.done;
    if (key === "access")     return !!form.enrollment_type;
    if (key === "quiz")       return checklist.find((c) => c.key === "quiz_type")?.done;
    if (key === "pricing")    return true;  // Free is auto-set
    if (key === "validation") return true;  // Optional
    if (key === "publish")    return true;
    return false;
}
